import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  NotebookPen, ShieldCheck, ShieldAlert, Copy, Check, RefreshCcw,
  Tag as TagIcon, Plus, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminInventorNotebook() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [copiedHash, setCopiedHash] = useState(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = tagFilter ? `?tag=${encodeURIComponent(tagFilter)}` : '';
      const res = await axios.get(
        `${API_URL}/api/admin/inventor-notebook/entries${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(res.data?.items || []);
    } catch {
      toast.error('Failed to load notebook');
    } finally {
      setLoading(false);
    }
  }, [tagFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const cleanedTags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    if (title.trim().length < 4) {
      toast.error('Give it a real title (4+ chars).');
      return;
    }
    if (body.trim().length < 20) {
      toast.error('Describe the idea in at least 20 characters.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/inventor-notebook/entries`,
        { title: title.trim(), body: body.trim(), tags: cleanedTags },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Entry sealed · ${res.data.content_sha256.slice(0, 12)}…`);
      setTitle('');
      setBody('');
      setTagsInput('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const copyHash = async (hash) => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopiedHash(hash);
      setTimeout(() => setCopiedHash(null), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const allTags = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => (e.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [entries]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="inventor-notebook-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <NotebookPen className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="text-2xl font-bold text-white">
                Inventor's Notebook
              </h1>
            </div>
            <p className="text-sm text-white/60 max-w-2xl">
              Append-only conception-date trail for patent prep. Every
              entry is sealed with a SHA-256 hash and a UTC timestamp the
              moment you save it. Hashes auto-verify on every read — any
              tampered row is flagged in red.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="inventor-notebook-refresh-btn"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* New entry form */}
        <form
          onSubmit={submit}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4"
          data-testid="inventor-notebook-new-form"
        >
          <div className="flex items-center gap-2 text-[#00F0FF] text-xs font-bold uppercase tracking-wider">
            <Plus className="h-4 w-4" /> New conception entry
          </div>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Idea title (e.g. 'Auto-rotating webhook secrets keyed by purchase')"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={180}
              className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white placeholder:text-white/30 focus:border-[#00F0FF] outline-none"
              data-testid="inventor-notebook-title-input"
            />
            <textarea
              placeholder="Describe the idea in enough detail that an attorney could anchor a priority date from this entry alone. Include: the problem, the inventive step, what's different from prior art, and any rough implementation notes."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white placeholder:text-white/30 focus:border-[#00F0FF] outline-none font-mono text-sm leading-relaxed"
              data-testid="inventor-notebook-body-input"
            />
            <input
              type="text"
              placeholder="Tags (comma-separated, e.g. payouts, vetting, api-delivery)"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white placeholder:text-white/30 focus:border-[#00F0FF] outline-none text-sm"
              data-testid="inventor-notebook-tags-input"
            />
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-white/40">
              Sealing creates a permanent, hash-verified row. Edits and
              deletes are <span className="text-white/70 font-semibold">disabled by design</span> —
              that's the whole point.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="inventor-notebook-seal-btn"
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sealing…</>
              ) : (
                <><ShieldCheck className="h-4 w-4 mr-2" />Seal entry</>
              )}
            </Button>
          </div>
        </form>

        {/* Tag filter */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="inventor-notebook-tag-filter">
            <span className="text-xs text-white/40 uppercase font-semibold tracking-wider">
              Filter:
            </span>
            <button
              type="button"
              onClick={() => setTagFilter('')}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                tagFilter === ''
                  ? 'bg-[#00F0FF] text-black border-[#00F0FF]'
                  : 'bg-transparent text-white/70 border-white/20 hover:border-white/40'
              }`}
              data-testid="inventor-notebook-filter-all"
            >
              All ({entries.length})
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setTagFilter(tag)}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                  tagFilter === tag
                    ? 'bg-[#00F0FF] text-black border-[#00F0FF]'
                    : 'bg-transparent text-white/70 border-white/20 hover:border-white/40'
                }`}
                data-testid={`inventor-notebook-filter-${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Entries */}
        <div className="space-y-3" data-testid="inventor-notebook-list">
          {loading && entries.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/50 text-sm">
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center">
              <NotebookPen className="h-8 w-8 mx-auto text-white/30 mb-3" />
              <p className="text-white/60 text-sm">
                No entries yet. The first one you save above will be the
                anchor priority date for that idea.
              </p>
            </div>
          ) : (
            entries.map((e) => (
              <article
                key={e.entry_id}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                data-testid={`inventor-entry-${e.entry_id}`}
              >
                <header className="flex items-start justify-between gap-4 flex-wrap mb-2">
                  <h3 className="text-white font-bold text-lg leading-snug">
                    {e.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    {e.hash_verified === false ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-semibold">
                        <ShieldAlert className="h-3 w-3" />
                        Hash mismatch
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/30 font-semibold">
                        <ShieldCheck className="h-3 w-3" />
                        Hash verified
                      </span>
                    )}
                  </div>
                </header>

                <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap mb-3">
                  {e.body}
                </p>

                {(e.tags || []).length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mb-3">
                    {e.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                      >
                        <TagIcon className="h-2.5 w-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <footer className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-white/5">
                  <div className="text-[11px] text-white/40 font-mono">
                    {new Date(e.created_at).toUTCString()}
                    {' · '}
                    {e.created_by_email}
                  </div>
                  <button
                    type="button"
                    onClick={() => copyHash(e.content_sha256)}
                    className="group inline-flex items-center gap-1.5 text-[11px] font-mono text-white/50 hover:text-white transition"
                    data-testid={`inventor-entry-${e.entry_id}-copy-hash`}
                    title="Copy SHA-256 hash"
                  >
                    {copiedHash === e.content_sha256 ? (
                      <><Check className="h-3 w-3 text-[#00FF94]" />copied</>
                    ) : (
                      <><Copy className="h-3 w-3" />{e.content_sha256.slice(0, 16)}…</>
                    )}
                  </button>
                </footer>
              </article>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminInventorNotebook;
